import React, { useRef, useState } from 'react';
import FileAttachment from './FileAttachment';

function formatTime(ts) {
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function initials(username) {
  return username ? username.slice(0, 2).toUpperCase() : '??';
}

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

function LinkedText({ text }) {
  const parts = text.split(URL_REGEX);
  return (
    <>
      {parts.map((part, i) =>
        URL_REGEX.test(part) ? (
          <a key={i} href={part} target="_blank" rel="noopener noreferrer"
            className="underline underline-offset-2 opacity-90 hover:opacity-100 break-all"
            onClick={(e) => e.stopPropagation()}>{part}</a>
        ) : <span key={i}>{part}</span>
      )}
    </>
  );
}

const avatarColors = ['bg-violet-600', 'bg-indigo-600', 'bg-sky-600', 'bg-teal-600', 'bg-emerald-600', 'bg-pink-600', 'bg-rose-600'];
function avatarColor(username) {
  return username ? avatarColors[username.charCodeAt(0) % avatarColors.length] : 'bg-slate-600';
}

function ReplyPreview({ replyTo, senderName, isOwn, onScrollToMessage }) {
  const label = replyTo.deleted ? 'Deleted message'
    : replyTo.message_type === 'file' ? `📎 ${replyTo.file_name || 'File'}`
    : replyTo.content;
  return (
    <div
      onClick={() => onScrollToMessage?.(replyTo.id)}
      className={`mb-1.5 rounded-lg border-l-2 px-2 py-1 text-xs cursor-pointer active:opacity-70 transition-opacity ${isOwn ? 'border-indigo-300 bg-indigo-700/40' : 'border-indigo-400 bg-white/10'}`}
    >
      <p className="font-semibold opacity-80">{senderName}</p>
      <p className="opacity-60 truncate max-w-[180px]">{label}</p>
    </div>
  );
}

function ReadTick({ readAt }) {
  // Single tick = sent, double tick = read
  const color = readAt ? 'text-indigo-300' : 'text-indigo-400/60';
  return (
    <span className={`inline-flex items-center ${color}`} title={readAt ? 'Read' : 'Sent'}>
      <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="currentColor">
        {readAt ? (
          // Double tick
          <path d="M1 8l4 4L11 4M5 8l4 4 5-8" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        ) : (
          // Single tick
          <path d="M3 8l4 4 6-7" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        )}
      </svg>
    </span>
  );
}

const EMOJI_LIST = ['👍', '❤️', '😂', '😮', '😢', '🔥', '👏', '😏'];

function EmojiPicker({ onPick, onClose }) {
  return (
    <div className="flex gap-1 rounded-full border border-white/10 bg-[#1e2433] px-2 py-1.5 shadow-xl">
      {EMOJI_LIST.map((e) => (
        <button key={e} onClick={() => { onPick(e); onClose(); }}
          className="text-lg hover:scale-125 transition-transform leading-none">
          {e}
        </button>
      ))}
    </div>
  );
}

export default function MessageBubble({ message, isOwn, senderName, friendName, apiUrl, token, reactions, currentUserId, onReply, onEdit, onDelete, onReact, onScrollToMessage }) {
  const isFile = message.message_type === 'file';
  const isDeleted = message.deleted;

  const touchStartX = useRef(null);
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const longPressTimer = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    longPressTimer.current = setTimeout(() => {
      setMenuOpen(true);
      // Cancel any pending selection the browser started
      if (window.getSelection) window.getSelection().removeAllRanges();
    }, 500);
  };

  const onTouchMove = (e) => {
    if (touchStartX.current === null) return;
    clearTimeout(longPressTimer.current);
    const dx = e.touches[0].clientX - touchStartX.current;
    const dir = isOwn ? -1 : 1;
    const clamped = Math.max(0, Math.min(dx * dir, 72));
    if (clamped > 5) setSwiping(true);
    setSwipeX(clamped * dir);
  };

  const onTouchEnd = () => {
    clearTimeout(longPressTimer.current);
    if (Math.abs(swipeX) >= 60) onReply?.(message);
    setSwipeX(0); setSwiping(false);
    touchStartX.current = null;
  };

  const handleContextMenu = (e) => { e.preventDefault(); setMenuOpen(true); };

  // Group reactions: {emoji: [usernames]}
  const reactionGroups = {};
  for (const r of (reactions || [])) {
    if (!reactionGroups[r.emoji]) reactionGroups[r.emoji] = [];
    reactionGroups[r.emoji].push(r.username);
  }
  const myReaction = (reactions || []).find((r) => r.user_id === currentUserId)?.emoji;

  return (
    <div id={`msg-${message.id}`} className={`relative flex items-end gap-2 ${isOwn ? 'flex-row-reverse' : 'flex-row'} mb-1 group`}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      {/* Swipe hint arrow */}
      {swiping && Math.abs(swipeX) > 20 && (
        <div className={`absolute ${isOwn ? 'left-2' : 'right-2'} top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-full bg-white/20`}>
          <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
          </svg>
        </div>
      )}

      {!isOwn && (
        <div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${avatarColor(senderName)}`}>
          {initials(senderName)}
        </div>
      )}

      <div
        className={`flex max-w-[70%] flex-col gap-0.5 ${isOwn ? 'items-end' : 'items-start'}`}
        style={{
          transform: `translateX(${swipeX}px)`,
          transition: swiping ? 'none' : 'transform 0.2s ease',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          WebkitTouchCallout: 'none',
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onContextMenu={handleContextMenu}
      >
        {/* Hover action toolbar — floats above the bubble, Teams-style */}
        {!isDeleted && hovered && (
          <div className={`hidden md:flex absolute -top-8 ${isOwn ? 'right-0' : 'left-0'} z-10 items-center gap-0.5 rounded-full border border-white/10 bg-[#1e2433] px-1 py-0.5 shadow-lg`}>
            <button onClick={() => onReply?.(message)} title="Reply"
              className="rounded-full p-1.5 text-slate-400 hover:bg-white/10 hover:text-white transition">
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
              </svg>
            </button>
            <button onClick={() => setEmojiOpen((o) => !o)} title="React"
              className="rounded-full p-1.5 text-slate-400 hover:bg-white/10 hover:text-white transition">
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" />
              </svg>
            </button>
            {isOwn && message.message_type === 'text' && (
              <button onClick={() => onEdit?.(message)} title="Edit"
                className="rounded-full p-1.5 text-slate-400 hover:bg-white/10 hover:text-white transition">
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                </svg>
              </button>
            )}
          </div>
        )}

        <div className={`relative px-3 py-2 shadow-sm
          ${isOwn ? 'rounded-2xl rounded-br-none bg-indigo-600 text-white' : 'rounded-2xl rounded-bl-none bg-[#273047] text-slate-100'}
          ${isFile && !isDeleted ? 'p-2' : ''}`}>

          {message.reply_to && !isDeleted && (
            <ReplyPreview
              replyTo={message.reply_to}
              senderName={message.reply_to.sender_id === message.sender_id ? senderName : (isOwn ? friendName : 'You')}
              isOwn={isOwn}
              onScrollToMessage={onScrollToMessage}
            />
          )}

          {isDeleted ? (
            <p className="text-[13px] italic opacity-50">This message was deleted</p>
          ) : isFile ? (
            <>
              <FileAttachment fileUrl={message.file_url} fileName={message.file_name}
                fileSize={message.file_size} fileMime={message.file_mime} apiUrl={apiUrl} token={token} />
              {message.content && (
                <p className="mt-1.5 break-words text-[13px] leading-snug whitespace-pre-wrap px-1">
                  <LinkedText text={message.content} />
                </p>
              )}
            </>
          ) : (
            <p className="break-words text-[14px] leading-snug whitespace-pre-wrap">
              <LinkedText text={message.content} />
            </p>
          )}

          <span className={`mt-1 flex items-center justify-end gap-1 text-[10px] leading-none ${isOwn ? 'text-indigo-200' : 'text-slate-500'}`}>
            {message.edited && !isDeleted && <span className="opacity-70">edited</span>}
            {formatTime(message.created_at)}
            {isOwn && !isDeleted && <ReadTick readAt={message.read_at} />}
          </span>
        </div>

        {/* Reactions display */}
        {Object.keys(reactionGroups).length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-0.5 ${isOwn ? 'justify-end' : 'justify-start'}`}>
            {Object.entries(reactionGroups).map(([emoji, users]) => (
              <button
                key={emoji}
                onClick={() => onReact?.(message.id, emoji)}
                title={users.join(', ')}
                className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs border transition
                  ${myReaction === emoji
                    ? 'bg-indigo-600/30 border-indigo-500/50 text-white'
                    : 'bg-white/10 border-white/10 text-slate-300 hover:bg-white/20'}`}
              >
                <span>{emoji}</span>
                {users.length > 1 && <span>{users.length}</span>}
              </button>
            ))}
          </div>
        )}

        {/* Emoji picker (shown above the bubble) */}
        {emojiOpen && (
          <div className={`absolute bottom-full mb-2 z-50 ${isOwn ? 'right-0' : 'left-8'}`}>
            <EmojiPicker onPick={(emoji) => onReact?.(message.id, emoji)} onClose={() => setEmojiOpen(false)} />
          </div>
        )}
      </div>

      {/* Context menu */}
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
          <div className={`absolute z-50 bottom-full mb-1 ${isOwn ? 'right-0' : 'left-8'} w-44 rounded-xl border border-white/10 bg-[#1e2433] shadow-xl py-1 overflow-hidden`}>
            {!isDeleted && (
              <>
                {/* Emoji row at top of menu */}
                <div className="flex flex-wrap justify-center gap-1 px-2 py-2 border-b border-white/10">
                  {EMOJI_LIST.map((e) => (
                    <button key={e} onClick={() => { onReact?.(message.id, e); setMenuOpen(false); }}
                      className={`text-lg hover:scale-125 transition-transform leading-none rounded-full p-0.5 ${myReaction === e ? 'bg-indigo-600/40 ring-1 ring-indigo-500' : ''}`}>
                      {e}
                    </button>
                  ))}
                </div>
                <button onClick={() => { onReply?.(message); setMenuOpen(false); }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-white/10">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                  </svg>
                  Reply
                </button>
              </>
            )}
            {isOwn && !isDeleted && message.message_type === 'text' && (
              <button onClick={() => { onEdit?.(message); setMenuOpen(false); }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-white/10">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                </svg>
                Edit
              </button>
            )}
            {isOwn && !isDeleted && (
              <button onClick={() => { onDelete?.(message); setMenuOpen(false); }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                </svg>
                Delete
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
